import { useCallback, useState } from "react";
import { newLocalAttempt, submitLocally } from "../data/localAttempt";
import useAttemptSteps from "./useAttemptSteps";

// A session that writes nothing anywhere: the teacher's preview and My
// Learning's "Practise again" (Handoff 007 §8). It runs the very same step
// lifecycle, screens and feedback rules as real work, against an attempt that
// lives only in memory. No attempt, evidence, review, progress or submission
// record is created, so nothing reaches a teacher's work to review.
const start = (activity) => ({
  phase: "activity",
  activity,
  attempt: newLocalAttempt(),
  stepIndex: 0,
  verdict: null,
  runtime: {},
});

const useLocalSession = (activity, policy) => {
  const [state, setState] = useState(() => start(activity));

  // Memory never fails, so there is nothing to retry.
  const run = useCallback(async (action) => {
    await action();
  }, []);

  // Pictures stay on screen; they are never uploaded.
  const submit = useCallback(
    async (attempt, { stepId, submissionId, response }) =>
      submitLocally(state.activity, attempt, { stepId, submissionId, response }, policy),
    [state.activity, policy]
  );

  const finish = useCallback(() => ({ phase: "finished" }), []);

  const { completeStep, saveProgress } = useAttemptSteps({
    state,
    setState,
    submit,
    run,
    finish,
    // Practice and preview leave nothing behind, not even on this tablet.
    keepOnDevice: false,
  });

  return {
    ...state,
    saving: false,
    retry: null,
    completeStep,
    saveProgress,
    restart: () => setState(start(activity)),
    unavailable: () => setState({ phase: "unavailable" }),
  };
};

export default useLocalSession;
